import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { ExternalLink, FileDown, RotateCw } from "lucide-react";

// The handbook is a static page in public/handbook, written from the application's own screens and
// rules, framed here so the sidebar stays in reach. nginx answers a file that is not there with the
// app's index.html, and so does the dev server — which would draw Abhyas inside Abhyas and read as a
// handbook with nothing in it. So the page must carry its marker; nothing else counts as loaded.
const HANDBOOK = "/handbook/";
const PDF = "/handbook/Abhyas-HR-Handbook.pdf";
const MARKER = 'meta[name="abhyas-handbook"]';

export default function Documentation() {
  const { theme } = useTheme();
  const { hash } = useLocation();
  const frame = useRef(null);
  const [state, setState] = useState("loading"); // loading | ready | failed
  const [attempt, setAttempt] = useState(0);

  // Same origin, so the app's theme is handed to the page rather than the page guessing from the
  // system setting and disagreeing with the switch in the header.
  const applyTheme = () => {
    const doc = frame.current?.contentDocument;
    if (doc?.documentElement) doc.documentElement.setAttribute("data-theme", theme);
  };

  const onLoad = () => {
    let doc = null;
    try {
      doc = frame.current?.contentDocument;
    } catch {
      doc = null;
    }
    if (!doc || !doc.querySelector(MARKER)) {
      setState("failed");
      return;
    }
    applyTheme();
    setState("ready");
  };

  useEffect(() => {
    if (state === "ready") applyTheme();
  }, [theme]); // eslint-disable-line react-hooks/exhaustive-deps

  const retry = () => {
    setState("loading");
    setAttempt((n) => n + 1);
  };

  return (
    <div className="space-y-4" data-testid="documentation-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="overline text-primary">Help</div>
          <h2 className="font-heading font-bold text-3xl mt-1">Documentation</h2>
          <p className="text-sm text-muted-foreground mt-1">
            How each screen works and the rules behind it. The screens in it show sample data, not your records.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" asChild>
            <a href={HANDBOOK} target="_blank" rel="noopener noreferrer" data-testid="documentation-new-tab">
              <ExternalLink className="w-4 h-4 mr-1.5" /> Open in a new tab
            </a>
          </Button>
          <Button variant="outline" asChild>
            <a href={PDF} download data-testid="documentation-pdf">
              <FileDown className="w-4 h-4 mr-1.5" /> Download PDF
            </a>
          </Button>
        </div>
      </div>

      <div className="relative rounded-lg border border-border bg-card overflow-hidden"
           style={{ height: "calc(100dvh - 13rem)", minHeight: "28rem" }}>
        {state !== "failed" && (
          <iframe ref={frame} key={attempt} title="Abhyas handbook" src={`${HANDBOOK}?v=${attempt}${hash || ""}`}
                  onLoad={onLoad} className={`w-full h-full ${state === "loading" ? "invisible" : ""}`}
                  data-testid="documentation-frame" />
        )}
        {state === "loading" && (
          <div className="absolute inset-0 grid place-items-center text-sm text-muted-foreground">
            Loading the handbook...
          </div>
        )}
        {state === "failed" && (
          <div className="absolute inset-0 grid place-items-center p-6 text-center" data-testid="documentation-failed">
            <div>
              <div className="text-sm text-destructive-text">The handbook could not be loaded.</div>
              <div className="text-xs text-muted-foreground mt-0.5">Nothing is wrong with your records — only this page is missing.</div>
              <Button variant="outline" size="sm" className="mt-3" onClick={retry}>
                <RotateCw className="w-4 h-4 mr-1.5" /> Try again
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
